import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { 
  Bell, Clock, Calendar, Save, RefreshCw, 
  AlertCircle, CheckCircle
} from 'lucide-react';
import './ReminderSettings.css';

const ReminderSettings = () => {
  const { request } = useApi();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    isEnabled: true,
    reminder1Delay: 30,
    reminder2Delay: 300,
    reminder3Delay: 1440
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [preview, setPreview] = useState(null);

  const orgName = localStorage.getItem('wa_org_name') || user?.organizationName || 'Your Organization';

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const data = await request('get', '/api/reminder/config');
      
      const isEnabled = data.isEnabled !== undefined ? data.isEnabled : true;
      
      setConfig({
        isEnabled: isEnabled,
        reminder1Delay: data.reminder1Delay || 30,
        reminder2Delay: data.reminder2Delay || 300,
        reminder3Delay: data.reminder3Delay || 1440
      });
      updatePreview(data);
      
      setMessage({ type: 'success', text: `Reminders ${isEnabled ? 'enabled' : 'disabled'}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const updatePreview = (data) => {
    const d1 = data?.reminder1Delay || 30;
    const d2 = data?.reminder2Delay || 300;
    const d3 = data?.reminder3Delay || 1440;
    
    setPreview({
      first: formatTime(d1),
      second: formatTime(d2),
      third: formatTime(d3)
    });
  };

  const formatTime = (minutes) => {
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return days > 0 ? `${days}d ${hours}h` : `${hours}h`;
    } else if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  const handleChange = (field, value) => {
    if (value === '') {
      setConfig(prev => ({ ...prev, [field]: '' }));
      const tempConfig = { ...config, [field]: 0 };
      updatePreview(tempConfig);
      return;
    }

    let cleanedValue = value;
    if (value.length > 1 && value.startsWith('0')) {
      cleanedValue = value.replace(/^0+/, '');
      if (cleanedValue === '') cleanedValue = '0';
    }

    const numValue = parseInt(cleanedValue);
    if (isNaN(numValue) || numValue < 0) return;
    
    setConfig(prev => ({ ...prev, [field]: numValue }));
    updatePreview({ ...config, [field]: numValue });
  };

  const handleToggle = () => {
    const newValue = !config.isEnabled;
    setConfig(prev => ({ ...prev, isEnabled: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    const d1 = config.reminder1Delay === '' ? 0 : parseInt(config.reminder1Delay) || 0;
    const d2 = config.reminder2Delay === '' ? 0 : parseInt(config.reminder2Delay) || 0;
    const d3 = config.reminder3Delay === '' ? 0 : parseInt(config.reminder3Delay) || 0;

    if (d1 < 1) {
      setMessage({ type: 'error', text: 'First reminder must be at least 1 minute' });
      setSaving(false);
      return;
    }

    if (d2 <= d1) {
      setMessage({ type: 'error', text: 'Second reminder must be after the first reminder' });
      setSaving(false);
      return;
    }

    if (d3 <= d2) {
      setMessage({ type: 'error', text: 'Third reminder must be after the second reminder' });
      setSaving(false);
      return;
    }

    try {
      const payload = {
        isEnabled: config.isEnabled === true,
        reminder1Delay: d1,
        reminder2Delay: d2,
        reminder3Delay: d3
      };
      
      await request('post', '/api/reminder/config', payload);
      
      setConfig(prev => ({
        ...prev,
        reminder1Delay: d1,
        reminder2Delay: d2,
        reminder3Delay: d3
      }));
      
      setMessage({ type: 'success', text: `✅ Reminders ${config.isEnabled ? 'enabled' : 'disabled'} successfully!` });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (err) {
      setMessage({ type: 'error', text: '❌ Failed to save settings: ' + (err.response?.data?.message || err.message) });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      await request('post', '/api/reminder/test');
      setMessage({ type: 'success', text: '✅ Reminder check triggered! Check logs for details.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (err) {
      setMessage({ type: 'error', text: '❌ Failed to trigger reminder check' });
    }
  };

  const getDisplayValue = (field) => {
    const val = config[field];
    return val === '' ? '' : val;
  };

  if (loading) {
    return (
      <div className="reminder-settings-loading">
        <div className="loading-spinner"></div>
        <p>Loading your reminder settings...</p>
      </div>
    );
  }

  return (
    <div className="reminder-settings">
      {/* Header */}
      <div className="reminder-header">
        <div className="reminder-header-content">
          <div className="reminder-header-icon">
            <Bell size={24} />
          </div>
          <div>
            <h1 className="reminder-title">Reminder Settings</h1>
            <p className="reminder-subtitle">
              Configure automated reminders for {orgName}
            </p>
          </div>
        </div>
        <div className="reminder-header-actions">
          <button 
            className="btn-test" 
            onClick={handleTest}
            title="Test reminder system"
          >
            <RefreshCw size={16} />
            Test System
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`reminder-message ${message.type}`}>
          {message.type === 'success' ? (
            <CheckCircle size={18} />
          ) : (
            <AlertCircle size={18} />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <div className="reminder-grid">
        {/* Main Settings Card */}
        <div className="reminder-main-card">
          <form onSubmit={handleSubmit}>
            {/* Toggle */}
            <div className="reminder-toggle-section">
              <div className="reminder-toggle">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={config.isEnabled}
                    onChange={handleToggle}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <div className="toggle-info">
                  <span className="toggle-label">
                    {config.isEnabled ? '✅ Reminders Enabled' : '⛔ Reminders Disabled'}
                  </span>
                  <span className="toggle-description">
                    {config.isEnabled 
                      ? 'Automatic reminders will be sent to your customers' 
                      : 'No reminders will be sent until you enable this'}
                  </span>
                </div>
              </div>
            </div>

            <div className="divider"></div>

            {/* Delay Settings */}
            <div className="delays-section">
              <h3 className="section-title">
                <Clock size={18} />
                Reminder Timing
              </h3>
              <p className="section-description">
                Set when each reminder should be sent after customer inactivity
              </p>

              <div className="delays-grid">
                {/* Reminder 1 */}
                <div className="delay-card delay-1">
                  <div className="delay-header">
                    <div className="delay-number">1</div>
                    <div className="delay-info">
                      <span className="delay-name">First Reminder</span>
                      <span className="delay-preview">{preview?.first}</span>
                    </div>
                  </div>
                  <div className="delay-input-group">
                    <input
                      type="text"
                      className="delay-input"
                      value={getDisplayValue('reminder1Delay')}
                      onChange={(e) => handleChange('reminder1Delay', e.target.value)}
                      placeholder="Enter minutes"
                      disabled={!config.isEnabled}
                    />
                    <span className="delay-unit">minutes</span>
                  </div>
                  <div className="delay-hint">
                    After customer's last message
                  </div>
                </div>

                {/* Reminder 2 */}
                <div className="delay-card delay-2">
                  <div className="delay-header">
                    <div className="delay-number">2</div>
                    <div className="delay-info">
                      <span className="delay-name">Second Reminder</span>
                      <span className="delay-preview">{preview?.second}</span>
                    </div>
                  </div>
                  <div className="delay-input-group">
                    <input
                      type="text"
                      className="delay-input"
                      value={getDisplayValue('reminder2Delay')}
                      onChange={(e) => handleChange('reminder2Delay', e.target.value)}
                      placeholder="Enter minutes"
                      disabled={!config.isEnabled}
                    />
                    <span className="delay-unit">minutes</span>
                  </div>
                  <div className="delay-hint">
                    After first reminder
                  </div>
                </div>

                {/* Reminder 3 */}
                <div className="delay-card delay-3">
                  <div className="delay-header">
                    <div className="delay-number">3</div>
                    <div className="delay-info">
                      <span className="delay-name">Third Reminder</span>
                      <span className="delay-preview">{preview?.third}</span>
                    </div>
                  </div>
                  <div className="delay-input-group">
                    <input
                      type="text"
                      className="delay-input"
                      value={getDisplayValue('reminder3Delay')}
                      onChange={(e) => handleChange('reminder3Delay', e.target.value)}
                      placeholder="Enter minutes"
                      disabled={!config.isEnabled}
                    />
                    <span className="delay-unit">minutes</span>
                  </div>
                  <div className="delay-hint">
                    After second reminder
                  </div>
                </div>
              </div>
            </div>

            <div className="divider"></div>

            {/* Timeline Preview */}
            <div className="timeline-preview">
              <h4 className="timeline-title">
                <Calendar size={16} />
                Timeline Preview
              </h4>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-dot green"></div>
                  <div className="timeline-content">
                    <span className="timeline-event">Customer Message</span>
                    <span className="timeline-time">Now</span>
                  </div>
                </div>
                <div className="timeline-connector"></div>
                <div className="timeline-item">
                  <div className="timeline-dot blue"></div>
                  <div className="timeline-content">
                    <span className="timeline-event">Reminder 1</span>
                    <span className="timeline-time">{preview?.first || '30m'}</span>
                  </div>
                </div>
                <div className="timeline-connector"></div>
                <div className="timeline-item">
                  <div className="timeline-dot orange"></div>
                  <div className="timeline-content">
                    <span className="timeline-event">Reminder 2</span>
                    <span className="timeline-time">{preview?.second || '5h'}</span>
                  </div>
                </div>
                <div className="timeline-connector"></div>
                <div className="timeline-item">
                  <div className="timeline-dot red"></div>
                  <div className="timeline-content">
                    <span className="timeline-event">Reminder 3</span>
                    <span className="timeline-time">{preview?.third || '24h'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="divider"></div>

            {/* Actions */}
            <div className="form-actions">
              <button 
                type="button" 
                className="btn-reset"
                onClick={fetchConfig}
                disabled={saving}
              >
                <RefreshCw size={16} />
                Reset
              </button>
              <button 
                type="submit" 
                className="btn-save"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-small"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReminderSettings;